import { useState } from "react";
import {
  CheckCircle2,
  Sparkles,
  BarChart3,
  TrendingUp,
  Mail,
  Briefcase,
  CreditCard,
  Shield,
  Info,
  CalendarDays,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/constants";
import api, { getErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SecurityTab from "@/components/plan/SecurityTab";
import AboutTab from "@/components/plan/AboutTab";
import BookDemoTab from "@/components/plan/BookDemoTab";

// ─── Static data ───────────────────────────────────────────────────────────────

// Connections (free tier) feature list — no extraction items
const CONNECTIONS_FEATURES = [
  "Automated daily scraping of every connected portal",
  "Searchable archive of every PDF ever pulled",
  "Email digests when new documents land",
  "Custom digest rules",
  "Capital-call / distribution / statement labels",
];

// Full Elementar additional features (shown after Lumos hero)
const FULL_FEATURES = [
  "Everything in Connections",
  "Rosetta — structured data extraction from every statement (NAV, capital calls, distributions)",
  "Per-fund position rollup — paid-in, distributed, NAV, TVPI/DPI",
  "Performance dashboards",
  "Full portfolio Q&A in Lumos",
];

const COMING_SOON = [
  {
    icon: BarChart3,
    title: "Performance Dashboards",
    desc: "TVPI / DPI / IRR charts, vintage cohort breakdowns, and J-curve overlays.",
  },
  {
    icon: TrendingUp,
    title: "Cash-Flow Forecasting",
    desc: "Projected capital calls and distributions across your committed portfolio.",
  },
  {
    icon: Briefcase,
    title: "Multi-Investor Roll-up",
    desc: "Consolidate trusts, LPs, and SPVs into a single family-level view.",
  },
  {
    icon: Mail,
    title: "Smart Weekly Digest",
    desc: "One email a week summarizing what changed and what needs your attention.",
  },
];

// ─── Tab definitions ───────────────────────────────────────────────────────────

const TABS = [
  { id: "plan", label: "Plan & Subscribe", icon: CreditCard },
  { id: "security", label: "Security", icon: Shield },
  { id: "about", label: "About", icon: Info },
  { id: "book-demo", label: "Book a Demo", icon: CalendarDays },
];

// ─── Lumos hero card (shared sub-component) ────────────────────────────────────

function LumosHero() {
  return (
    <div
      className="rounded-xl mb-6"
      style={{
        backgroundColor: "#0B0B0C",
        padding: "20px 22px",
        border: "1px solid rgba(255,255,255,0.07)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
      }}
    >
      {/* Top row: mark + wordmark + pill */}
      <div className="flex items-start gap-3 flex-wrap">
        {/* Starburst SVG mark */}
        <svg
          aria-hidden="true"
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ flexShrink: 0, marginTop: "2px" }}
        >
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
            const rad = (deg * Math.PI) / 180;
            const cx = 18, cy = 18;
            const inner = 5.4;
            const outer = 16.56;
            const x1 = cx + Math.cos(rad) * inner;
            const y1 = cy + Math.sin(rad) * inner;
            const x2 = cx + Math.cos(rad) * outer;
            const y2 = cy + Math.sin(rad) * outer;
            return (
              <line
                key={deg}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#F0EDE8"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            );
          })}
          <circle cx="18" cy="18" r="2.2" fill="#D14233" />
        </svg>

        {/* Wordmark + byline */}
        <div style={{ lineHeight: 1 }}>
          <div className="flex items-baseline gap-0.5">
            <span
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "1.125rem",
                fontWeight: 600,
                letterSpacing: "0.08em",
                color: "#F0EDE8",
              }}
            >
              LUMOS
            </span>
            <span style={{ color: "#D14233", fontSize: "1.25rem", fontWeight: 700 }}>.</span>
          </div>
          <p
            style={{
              fontSize: "9px",
              letterSpacing: "0.14em",
              color: "rgba(240,237,232,0.55)",
              marginTop: "5px",
              textTransform: "uppercase",
            }}
          >
            An Elementar Product
          </p>
        </div>

        {/* NOW INCLUDED pill */}
        <div style={{ marginLeft: "auto" }}>
          <span
            style={{
              fontSize: "9px",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#D14233",
              border: "1px solid #D14233",
              borderRadius: "9999px",
              padding: "2px 8px",
              whiteSpace: "nowrap",
            }}
          >
            Now included
          </span>
        </div>
      </div>

      {/* Tagline */}
      <p
        style={{
          marginTop: "14px",
          fontSize: "0.8125rem",
          color: "rgba(240,237,232,0.8)",
          lineHeight: "1.55",
        }}
      >
        Your AI assistant for your portfolio and documents — ask plain-English questions
        about holdings, cashflows, NAV, and capital calls, and find any document instantly.
      </p>
    </div>
  );
}

// ─── Plan tab (two-card layout) ────────────────────────────────────────────────

function PlanTab({ user }) {
  const navigate = useNavigate();
  const [portalLoading, setPortalLoading] = useState(false);

  const isFullPlan = user?.plan === "full";

  const handleSubscribe = () => navigate("/settings?tab=billing&upgrade=1");

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const res = await api.post("/billing/portal", { return_url: window.location.href });
      window.location.assign(res.data.url);
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to open billing portal"));
      setPortalLoading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Two-card grid — stacks on mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">

        {/* ── CONNECTIONS card (free) ── */}
        <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100 flex flex-col">
          {/* Header */}
          <div style={{ backgroundColor: "#1A1A2E" }} className="px-6 py-8 text-center">
            {/* Current plan badge */}
            {!isFullPlan && (
              <div className="mb-3 flex justify-center">
                <span
                  className="px-3 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.15)",
                    color: "rgba(255,255,255,0.9)",
                    border: "1px solid rgba(255,255,255,0.25)",
                  }}
                >
                  Current plan
                </span>
              </div>
            )}
            <p
              className="text-xl font-bold text-white tracking-wide"
              style={{ letterSpacing: "0.06em" }}
            >
              CONNECTIONS
            </p>
            <p className="mt-2 text-3xl font-bold text-white">Free</p>
            <p className="mt-2 text-xs text-white/60">
              Portal scraping, document archive, digests, and Lumos Q&A — at no cost.
            </p>
          </div>

          {/* Features */}
          <div className="bg-white px-6 py-6 flex-1">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
              What's included
            </p>

            {/* Lumos hero — belongs to the free tier */}
            <LumosHero />

            <ul className="space-y-3">
              {CONNECTIONS_FEATURES.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-gray-700">
                  <CheckCircle2
                    className="w-4 h-4 mt-0.5 shrink-0"
                    style={{ color: BRAND.bg }}
                  />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── FULL ELEMENTAR card ($500/mo) ── */}
        <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100 flex flex-col">
          {/* Header */}
          <div style={{ backgroundColor: BRAND.bg }} className="px-6 py-8 text-center">
            {/* Current plan badge */}
            {isFullPlan && (
              <div className="mb-3 flex justify-center">
                <span
                  className="px-3 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "rgba(255,255,255,0.95)",
                    border: "1px solid rgba(255,255,255,0.35)",
                  }}
                >
                  Current plan
                </span>
              </div>
            )}

            {/* BETA pill */}
            {!isFullPlan && (
              <div className="mb-3 flex justify-center">
                <span
                  className="px-3 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.15)",
                    color: "rgba(255,255,255,0.85)",
                    border: "1px solid rgba(255,255,255,0.25)",
                  }}
                >
                  BETA · Rosetta in trial
                </span>
              </div>
            )}

            <p
              className="text-xl font-bold text-white tracking-wide"
              style={{ letterSpacing: "0.06em" }}
            >
              FULL ELEMENTAR
            </p>
            <div className="mt-2 flex items-baseline justify-center gap-1">
              <span className="text-3xl font-bold text-white">$500</span>
              <span className="text-base text-white/70">/mo</span>
            </div>
            <p className="mt-2 text-xs text-white/60">
              Everything in Connections, plus Rosetta structured extraction and dashboards.
            </p>

            {/* CTA */}
            <div className="mt-5">
              {isFullPlan ? (
                <Button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  className="font-semibold px-6"
                  style={{ backgroundColor: "white", color: BRAND.text }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f3f3f3")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "white")
                  }
                >
                  {portalLoading ? "Opening..." : "Manage subscription"}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubscribe}
                  className="font-semibold px-6"
                  style={{ backgroundColor: "white", color: BRAND.text }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f3f3")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
                >
                  Subscribe
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>

          {/* Features */}
          <div className="bg-white px-6 py-6 flex-1">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">
              What's included
            </p>
            <ul className="space-y-3">
              {FULL_FEATURES.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-gray-700">
                  <CheckCircle2
                    className="w-4 h-4 mt-0.5 shrink-0"
                    style={{ color: BRAND.bg }}
                  />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Coming soon — beneath both cards */}
      <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
        <div className="px-8 py-5 border-b border-gray-100 flex items-center gap-2">
          <Sparkles className="w-4 h-4" style={{ color: BRAND.bg }} />
          <p className="font-semibold text-gray-900">Coming soon</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-gray-100">
          {COMING_SOON.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-white p-6">
              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: BRAND.soft }}
                >
                  <Icon className="w-4 h-4" style={{ color: BRAND.text }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{title}</p>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PlanPage() {
  const [activeTab, setActiveTab] = useState("plan");
  const { user } = useAuth();

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Plan</h1>
        <p className="text-sm text-gray-500 mt-1">
          Pricing, security, and how to get in touch
        </p>
      </div>

      {/* Tab bar — matches SettingsPage pattern */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === id
                ? "border-current"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === id ? { color: BRAND.text, borderColor: BRAND.text } : {}}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "plan" && <PlanTab user={user} />}
      {activeTab === "security" && <SecurityTab />}
      {activeTab === "about" && <AboutTab />}
      {activeTab === "book-demo" && <BookDemoTab />}
    </div>
  );
}
